/*************************************************************************
* ADOBE SYSTEMS INCORPORATED
*  Copyright 2016 Adobe Systems Incorporated
*  All Rights Reserved.
*
* NOTICE:  Adobe permits you to use, modify, and distribute this file in
* accordance with the terms of the Adobe license agreement accompanying it.
* If you have received this file from a source other than Adobe, then your
* use, modification, or distribution of it requires the prior written
* permission of Adobe.
**************************************************************************/

/*jslint node:true, nomen: true, maxlen:130, sub:true, todo:true */
/*eslint camelcase: 0, dot-notation: 0 */
/*global global, console, require, FormData */
'use strict';

var AdobeDC = require('./adobe-dc'),
    AdobeError = AdobeDC.AdobeError,
    handleJsonHttp = require('./adobe-http-handler').handleJsonHttp,
    http = handleJsonHttp(AdobeDC.http),
    discoveryEnabler;

// Functions are defined in reverse order of use to appease jslint.

function isObject(x) { return typeof x === "object"; }

/** 
 * Check if the object is empty, e.g. undefined, null, boolean false, number 0,
 * empty string "", empty array [], empty object {}.  It can't tell an empty function.
 */
function isEmpty(x) {
    if (!x) {
        // Handle undefined, null, boolean false, number 0, and empty string implicitly.
        return true;
    }
    if (Array.isArray(x)) {
        return x.length === 0;
    }
    if (isObject(x)) {
        return Object.keys(x).length === 0;
    }
    return false;
}

function assign(object, source) {
    Object.keys(source || {}).forEach(function (prop) {
        object[prop] = source[prop];
    });
    return object;
}

/**
 * This assembles the actual DC API call over http/XMLHttpRequest.
 * See discoveryEnabler for how it's actually exposed as:
 *     dcapi.call(operation, options) return Promise
 *
 * operation: The combined name of the resource and operation, e.g. "assets.createpdf".
 * options: An optional structure as below.
 *     accept:
 *         A short name to match in /discovery, e.g. "new_asset_v1.json".
 *     content_type:
 *         A short name to match in /discovery, e.g. "createpdf_parameters_v1.json".
 *     resource_parameter:
 *         The URI value to substitute for the resource in a template URI such as {+asset_download_uri}
 *     uri_parameters:
 *         Any parameters required for the URI e.g. size, format for renditions.
 *     headers:
 *         Headers for the HTTP request.  Callers should supply a unique "x-request-id" to supply
 *         a logging context.
 *     timeout:
 *         The timeout in milliseconds to wait for a response, unlimited by default.
 *     request_content_open:
 *         If true, leave the request open and available for writing/streaming.
 *         In this case, the request will be returned as a property of the returned promise.
 *         It's up to the caller to end the request. e.g. promise.request.end()
 *     response_content_open:
 *         If true, return the promise object returned by callAPI will resolve early with
 *         the http response object left open and content unprocessed.
 *         In this case, the caller will be responsible for setting up data handlers
 *         to process the returned data.
 *     content:
 *         The content to send over HTTP. JS objects will be converted to JSON
 *         if necessary, if the Content-Type is a flavour of application/json.
 *     boundary:
 *         If the request is a multi-part upload, this is the multi-part boundary string.
 *         This will be used to add the boundary parameter to the content type header.
 */
function callAPI(operation, options) {
    // TODO: Handle auto-refresh after discovery.expires.
    // TODO: Issue on('refresh', function) event after auto-refresh.
    if (isEmpty(operation)) {
        throw new AdobeError("Invalid operation: " + operation);
    }
    var self = this,
        pos = operation.indexOf('.'),
        reName = operation.substring(0, pos),
        opName = operation.substring(pos + 1),
        staticOptions = self.result.content.resources[reName],
        staticTemplates = self.result.content.templates || {},
        method,
        uri,
        accept,
        contentType,
        parameters = {},
        content,
        resource_parameter = null;

    if (staticOptions) {
        staticOptions = staticOptions[opName];
    }
    // Look for the resource parameter in:
    // - resource_parameter
    // - uri_parameters
    // - the resource parameter default
    options = options || {};
    if (staticOptions.resource_parameter) {
        resource_parameter = options.resource_parameter ||
            (isObject(options.uri_parameters) &&
             options.uri_parameters[staticOptions.resource_parameter.name]) ||
            staticOptions.resource_parameter["default"];
        // If there is no resource_parameter yet, try finding it from the templates if there are uri_parameters.
        if (!resource_parameter && isObject(options.uri_parameters)) {
            uri = staticTemplates[staticOptions.resource_parameter.name];
            if (uri) {
                resource_parameter = AdobeDC.uritemplate.parse(uri).expand(options.uri_parameters);
            }
        }
    }

    if (isEmpty(staticOptions)) {
        throw new AdobeError("Invalid operation: " + operation);
    }
    method = staticOptions.http_method;
    uri = staticOptions.uri;
    if (isEmpty(staticOptions.accept) && options.accept) {
        throw new AdobeError("Unexpected accept option provided for: " + operation);
    }
    if (!isEmpty(staticOptions.accept) && !options.accept) {
        throw new AdobeError("Missing accept option for: " + operation);
    }
    if (isEmpty(staticOptions.content_type) && options.content_type) {
        throw new AdobeError("Unexpected content_type option provided for: " + operation);
    }
    // Error on missing content_type unless for FormData that's handled automatically.
    if (!isEmpty(staticOptions.content_type) && !options.content_type &&
            !(/function|object/.test(typeof FormData) && options.content instanceof FormData)) {
        throw new AdobeError("Missing content_type option for: " + operation);
    }
    if (!isEmpty(staticOptions.resource_parameter) && !resource_parameter) {
        throw new AdobeError("Missing resource_parameter option for: " + operation);
    }
    if (isEmpty(staticOptions.resource_parameter) && options.resource_parameter) {
        throw new AdobeError("Unexpected resource_parameter option provided for: " + operation);
    }
    accept = options.accept ? staticOptions.accept[options.accept] : "*/*";
    contentType = options.content_type ? staticOptions.content_type[options.content_type] : undefined;

    if (contentType && contentType.indexOf("multipart") === 0 && !options.boundary) {
        throw new AdobeError("Missing boundary option for multipart content on: " + operation);
    }
    // Error on content with request_content_open unless for FormData that's handled automatically.
    if (options.request_content_open && options.content &&
            !(/function|object/.test(typeof FormData) && options.content instanceof FormData)) {
        throw new AdobeError("May not provide content with the request_content_open option for: " + operation);
    }
    content = options.content;

    if (resource_parameter) {
        // Handle RFC-6570 URI Template parameters; use late-binding to avoid dependency issues.
        parameters[staticOptions.resource_parameter.name] = resource_parameter;
    }

    // Some APIs like resolve_persistent_id don't have resource_parameter but have uri_parameters
    parameters = assign(parameters, options.uri_parameters);
    uri = AdobeDC.uritemplate.parse(uri).expand(parameters);

    options.headers = assign(options.headers || {}, {
        "x-api-app-info": self.config["x-api-app-info"]
    });
    ["x-api-client-id"].forEach(function (key) {
        if (self.config[key]) {
            options.headers[key] = self.config[key];
        }
    });

    // Add the auth header if it's required.
    // This uses Array.indexOf() since lodash/core does not have includes() nor does IE11 for Array.
    if (staticOptions.authentication && staticOptions.authentication.indexOf("auth_header_primary") !== -1) {
        if (typeof self.identityAccess.getCurrentToken === "function") {
            self.session.content = self.identityAccess.getCurrentToken();
        }
        options.headers.Authorization = "Bearer " + self.session.content.access_token;
    }
    if (accept) {
        options.headers.Accept = accept;
    }
    if (contentType) {
        options.headers["Content-Type"] = contentType;
    }
    return http.call(method, uri, options, content);
}

/**
 * This method getFinalURI prepares a final URI based on input parameters.
 * Applications will normally use this method to generate URIs that can be used as a `src` attribute
 * on HTML elements such as `img` or `iframe`.
 *
 * operation: The combined name of the resource and operation, e.g. "assets.download".
 * options: An optional structure as below.
 *     resource_parameter:
 *         The URI value to substitute for the resource in a template URI such as {+asset_download_uri}
 *     uri_parameters: any parameters required for the URI e.g. size, format for renditions
 */
function getFinalURI(operation, options) {
    if (isEmpty(operation)) {
        throw new AdobeError("Invalid operation: " + operation);
    }
    var self = this,
        pos = operation.indexOf('.'),
        reName = operation.substring(0, pos),
        opName = operation.substring(pos + 1),
        staticOptions = self.result.content.resources[reName],
        uri,
        parameters = {};

    if (staticOptions) {
        staticOptions = staticOptions[opName];
    }
    if (isEmpty(staticOptions)) {
        throw new AdobeError("Invalid operation: " + operation);
    }
    uri = staticOptions.uri;
    options = options || {};
    if (!options.resource_parameter) {
        throw new AdobeError("Missing resource_parameter option for: " + operation);
    }
    // Handle RFC-6570 URI Template parameters; use late-binding to avoid dependency issues.
    parameters[staticOptions.resource_parameter.name] = options.resource_parameter;
    parameters = assign(parameters, options.uri_parameters);
    uri = AdobeDC.uritemplate.parse(uri).expand(parameters);

    return uri;
}

/**
 * @description
 * Find the given URI-template in the /discovery response and apply the given parameters.
 * @method
 * @param {string} templateName - a template name available in discovery (typically "folder_uri" or "asset_uri")
 * @param {string} parameters - optional parameters object ({ `folder_id`: id } or  { `asset_id`: id })
 * @returns {string} a URI based on the URI-Template
 *
 * @example
 * var folderURN = result.content.folder_id;
 * // convert my URN to a URI that can be passed to dcapi
 * var folderUri = dcapi.getTemplateURI("folder_uri", { folder_id: folderURN }),
 * // I now have a folder uri that can be used with dcapi
 *
 * var assetURN = result.content.asset_id,
 * // Convert my asset URN to a URI that can be passed to dcapi
 * assetUri = dcapi.getTemplateURI("asset_uri", { asset_id: assetURN });
 * // I now have an asset uri that can be used with dcapi
 *
 * @public
 */
function getTemplateURI(templateName, parameters) {
    if (isEmpty(templateName)) {
        throw new AdobeError("Invalid templateName: " + templateName);
    }
    if (isEmpty(parameters)) {
        parameters = {};
    }
    var self = this,
        templates = self.result.content.templates,
        uri = templates ? templates[templateName] : undefined;

    if (isEmpty(uri)) {
        throw new AdobeError("Invalid templateName: " + templateName);
    }
    uri = AdobeDC.uritemplate.parse(uri).expand(parameters);

    return uri;
}

function getSchema(contentType) {
    var schema = contentType.replace(/"/g, "").match(/profile=(\S*)(;|$)/);
    if (schema === null) {
        throw new AdobeError("No schema profile found in: " + contentType);
    }

    return http.get(schema[1]).then(
        function (result) {
            return result.content;
        }
    );
}

/**
 * This method will format a part of a multipart upload.
 * It's only supported on NodeJS and uses lodash/template, not WebBrowserJS that only requires lodash/core.
 * Parameters:
 * operation: The combined name of the resource and operation, e.g. "assets.upload".
 *            The operation must have form_data_parameters defined in /discovery results.
 * options: a map of options used to format the output:
 *  options.parameterIndex (required):
 *      Indicate which form_data_parameter we're formatting
 *  options.contentType (required):
 *      Either a schema name (e.g. asset_upload_parameters_v1.json)
 *      or for file contents, the mime type of the file. e.g. "application/zip"
 *  options.boundary (required):
 *      The separator string for multi-part mime
 *  options.body:
 *      The contents to include in this part.  May be excluded if the caller wants to stream
 *      content.  If so, get the trailing boundary content with another call using
 *      parameterIndex + 1
 *  options.transferEncoding (optional):
 *      Used to populate a Content-Transfer-Encoding header.  e.g. "base64"
 *  options.filename (required for file parts):
 *      The name of the uploaded file
 */
function formDataPart(operation, options) {
    var parameter,
        CRLF = "\r\n",
        names,
        params = {contentType: options.contentType, boundary: options.boundary},
        template = [],
        body = options.body,
        startBoundary = function () { return '--' + params.boundary; },
        endBoundary = function () { return '--' + params.boundary + '--'; },
        type = function () { return "Content-Type: " + params.contentType; },
        disposition = function () {
            return 'Content-Disposition: form-data; name="' + params.name + '"';
        },
        dispositionFile = function () {
            return 'Content-Disposition: form-data; name="' + params.name +
                '"; filename="' + params.filename + '"';
        },
        transferEncoding = function () { return 'Content-Transfer-Encoding: ' + params.encoding; };

    try {
        names = operation.split(".");
        operation = this.result.content.resources[names[0]][names[1]];
        parameter = operation.form_data_parameters[options.parameterIndex];
    } catch (e) {
        throw new AdobeError("Invalid parameters to formDataPart.  Form data parameter not found");
    }
    // for the streaming case -- return the terminating boundary
    if (options.parameterIndex === operation.form_data_parameters.length) {
        return CRLF + endBoundary();
    }

    if (options.parameterIndex > 0) {
        template.push('');  // force a CRLF in front of the boundary
    }
    template.push(startBoundary());
    params.contentType = parameter.content_type ? parameter.content_type[options.contentType] : options.contentType;
    template.push(type());
    params.name = parameter.name;
    if (parameter.type === "object") {
        template.push(disposition());
        if (isObject(options.body)) {
            body = JSON.stringify(body);
        }
    }
    if (parameter.type === "file") {
        params.filename = options.filename;
        template.push(dispositionFile());
        if (options.transferEncoding) {
            params.encoding = options.transferEncoding;
            template.push(transferEncoding());
        }
    }
    template.push('');
    if (body) {
        params.content = body;
        template.push(params.content);
        // if it's the last part, add the ending boundary and CRLF
        if (options.parameterIndex + 1 === operation.form_data_parameters.length) {
            template.push(endBoundary());
            template.push('');
        }
    } else {
        template.push("");
    }
    return template.join(CRLF);
}

/*
 * Config file members:
 * configFile:
 *          The file path to a configuration file containing credentials
 * openHttpLog:
 *          A boolean flag to indicate whether to enable the built-in logger that
 *          records results in an http log file.  This is intended as a debugging aid
 *          to be used only in development
 * httpLogFilename:
 *          The name to use for the html log file
 * x-api-app-info:
 *          A setting used as an HTTP header in all requests to identify this application
 * x-request-id:
 *          A unique identified to use for logging the request to /discovery
 * identityAccess:
 *          A client-supplied implementation for authentication
 *          .getSessionToken() is required to return a Promise resolving to {status: ..., content: {access_token: ...}}
 *          .getCurrentToken() is optional to return {access_token: ...}
 * httpLogImplementation
 *          A client-supplied implementation to log http requests
 */
function API(config) {
    if (!(this instanceof API)) {
        return new API(config);
    }
    if (!config) {
        throw new AdobeError("config is required for new API(config)");
    }
    // Private closure variables.
    var self = this, promise, loadedConfig;

    if (typeof config.configFile === 'string' && AdobeDC.requireFile) {
        // Load and merge the config file when given config takes precedence.
        loadedConfig = AdobeDC.requireFile(config.configFile);
        assign(loadedConfig, config);
        config = loadedConfig;
    }
    self.config = config;

    function DiscoveryExecutor(fulfill, reject) {
        if (AdobeDC.HttpLogger) {
            self.openHttpLog = function (filename, loggerImplementation) {
                if (AdobeDC.http.logger) {
                    self.closeHttpLog();
                }

                if (loggerImplementation) {
                    AdobeDC.http.logger = loggerImplementation;
                } else {
                    AdobeDC.http.logger = new AdobeDC.HttpLogger(filename);
                }
            };
            self.closeHttpLog = function () {
                if (AdobeDC.http.logger) {
                    AdobeDC.http.logger.finish();
                    AdobeDC.http.logger = null;
                }
            };
        }
        if (!config["x-api-app-info"] && !config["x-api-client-id"]) {
            reject(new AdobeError('Missing "x-api-app-info" for AdobeDC config.'));
            return;
        }
        if (typeof config.identityAccess === 'object') {
            self.identityAccess = config.identityAccess;
        } else if (AdobeDC.IdentityAccess) {
            // Use the default if available and no instance was provided.
            self.identityAccess = new AdobeDC.IdentityAccess(config);
        }
        if (AdobeDC.HttpLogger && config.openHttpLog) {
            if (config.httpLogImplementation) {
                AdobeDC.http.logger = config.httpLogImplementation;
            } else {
                AdobeDC.http.logger = new AdobeDC.HttpLogger(config.httpLogFilename);
            }
            AdobeDC.http.maskData = !AdobeDC.http.logger.unmaskContent;
        }

        function fetchDiscovery() {
            var options = {
                headers: {
                    Accept: config.discovery.accept,
                    Authorization: "Bearer " + self.session.content.access_token
                }
            };
            ["x-request-id", "x-api-app-info", "x-api-client-id"].forEach(function (key) {
                if (config[key]) {
                    options.headers[key] = config[key];
                }
            });
            // Use config.discoveryPromise if available, otherwise get a fetch copy.
            // When building refresh capability, remember to ignore the original discoveryPromise.
            (config.discoveryPromise || http.get(config.discovery.uri, options)).then(function (result) {
                try {
                    discoveryEnabler(self, result);
                    fulfill(self);
                } catch (er) {
                    reject(er);
                }
            }, reject);
        }
        self.identityAccess.getSessionToken().then(function (result) {
            self.session = result;
            fetchDiscovery();
        }, reject);
    }

    // Set the public ready() function returning Promise for when ready with /discovery.
    self.ready = function () {
        if (!promise) {
            // Lazy initialization.
            promise = new AdobeDC.Promise(DiscoveryExecutor);
        }
        return promise;
    };
    // Alias dcapi.promise() for dcapi.ready().
    self.promise = self.ready;
    return this;
}

discoveryEnabler = function (self, result) {
    self.result = result;
    assign(self, result.content);
    self.call = function (operation, options) {
        // This uses the API.callAPI such that the callAPI can be wrapped/overridden.
        try {
            return API.callAPI.call(self, operation, options);
        } catch (err) {
            return AdobeDC.Promise.reject(err);
        }
    };
    self.getSchema = function (contentType) {
        try {
            return API.getSchema(contentType);
        } catch (err) {
            return AdobeDC.Promise.reject(err);
        }
    };
    self.formDataPart = function (operation, options) {
        return API.formDataPart.call(self, operation, options);
    };
    self.getFinalURI = function (operation, options) {
        return API.getFinalURI.call(self, operation, options);
    };
    self.getTemplateURI = function (templateName, parameters) {
        return API.getTemplateURI.call(self, templateName, parameters);
    };
};

assign(API, {
    callAPI: callAPI,
    getSchema: getSchema,
    formDataPart: formDataPart,
    getFinalURI: getFinalURI,
    getTemplateURI: getTemplateURI
});

module.exports = API;
